<template>
  <v-container class="automatic-building">
    <!-- Header with action buttons -->
    <div class="action-buttons">
      <v-btn color="primary" @click="openSelectionModal">
        <v-icon left>mdi-plus-circle</v-icon>
        Add buildings
      </v-btn>
    </div>

    <!-- Selected Buildings List -->
    <v-row>
      <v-col cols="12">
        <!-- Empty state -->
        <div v-if="selectedBuildingList.length === 0" class="empty-state">
          <v-icon size="80" color="primary">mdi-office-building-outline</v-icon>
          <h3>No Buildings Selected</h3>
          <p>Add existing buildings or create new ones to get started</p>
        </div>

        <div v-else class="buildings">
          <BuildingCard
            v-for="building in selectedBuildingList"
            :key="building.id"
            :building="building"
            @edit="openEditModal"
            @delete="removeFromSelectedList"
          />
        </div>
      </v-col>
    </v-row>

    <!-- Building Selection Modal -->
    <BuildingSelectionModal
      v-model="showSelectionModal"
      :buildings="buildings"
      :selected-building-ids="selectedBuildingIds"
      :lab-name="labName"
      @select="addBuildingToList"
      @create-new="openCreateModal"
      @toggle="toggleBuildingSelection"
      @close="showSelectionModal = false"
    />

    <!-- Building Edit/Create Modal -->
    <BuildingEditModal
      :value="showEditModal"
      :building="buildingToEdit"
      :saving="isSaving"
      @close="showEditModal = $event"
      @save="saveBuilding"
      @delete="confirmDeleteBuilding"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="450">
      <v-card class="confirm-dialog">
        <v-card-title class="confirm-dialog__title">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Confirm Deletion
        </v-card-title>
        <v-card-text class="confirm-dialog__content">
          <p>Are you sure you want to delete this building?</p>
          <p v-if="buildingToDelete" class="building-name">
            <strong>{{ buildingToDelete.name }}</strong>
          </p>
          <p class="warning-text">This action cannot be undone.</p>
        </v-card-text>
        <v-card-actions class="confirm-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteBuilding">
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      :color="snackbarColor"
      @click="snackbar = false"
      style="cursor: pointer"
    >
      <v-icon left v-if="snackbarColor === 'success'">
        mdi-check-circle
      </v-icon>
      <v-icon left v-else-if="snackbarColor === 'error'">
        mdi-alert-circle
      </v-icon>
      <v-icon left v-else> mdi-information </v-icon>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import BuildingCard from "./BuildingCard.vue";
import BuildingSelectionModal from "./BuildingSelectionModal.vue";
import BuildingEditModal from "./BuildingEditModal.vue";
import apiClient from "@/plugins/apiService";

export default {
  name: "AutomaticBuilding",

  components: {
    BuildingCard,
    BuildingSelectionModal,
    BuildingEditModal,
  },

  props: {
    selectedLab: {
      type: [String, Number],
      default: null,
    },
    selectedProblem: {
      type: [String, Number],
      default: null,
    },
    labName: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      // Buildings data
      buildings: [],
      selectedBuildingList: [],
      selectedBuildingId: null,

      // Modal states
      showSelectionModal: false,
      showEditModal: false,
      buildingToEdit: null,

      // Delete confirmation
      confirmDialog: false,
      buildingToDelete: null,

      // Loading states
      isSaving: false,

      // Snackbar
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
    };
  },

  computed: {
    selectedBuildingIds() {
      return this.selectedBuildingList.map((b) => b.id);
    },
  },

  mounted() {
    this.fetchBuildings();
  },

  methods: {
    // ==================== Modal Controls ====================
    openSelectionModal() {
      this.showSelectionModal = true;
    },

    openCreateModal() {
      this.buildingToEdit = null;
      this.showSelectionModal = false;
      this.showEditModal = true;
    },

    openEditModal(building) {
      this.buildingToEdit = { ...building };
      this.showEditModal = true;
    },

    // ==================== API Methods ====================
    async fetchBuildings() {
      if (!this.selectedLab) return;

      try {
        const response = await apiClient.get(`/building/${this.selectedLab}`, {
          params: { selectedProblem: this.selectedProblem },
        });
        const data = response.data;
        this.buildings = data;

        this.selectedBuildingList = this.buildings.filter(
          (building) => building.selectedBuilding
        );
      } catch (error) {
        console.error("Error fetching buildings:", error);
        this.buildings = [];
        this.showErrorMessage("Failed to load buildings");
      }
    },

    async saveBuilding(buildingData) {
      this.isSaving = true;

      try {
        const isUpdate =
          buildingData.id && !buildingData.id.toString().startsWith("temp-");
        const url = isUpdate ? `/building/${buildingData.id}` : `/building`;

        const payload = {
          name: buildingData.name,
          description: buildingData.description,
          address: buildingData.address,
          gps: buildingData.gps,
          selectedProblem: this.selectedProblem,
          selectedLab: this.selectedLab,
          filename: buildingData.modelFileName || "",
          date: new Date().toISOString().split("T")[0],
          version: 0,
        };

        const response = isUpdate
          ? await apiClient.put(url, payload)
          : await apiClient.post(url, payload);

        if (response.status === 200 || response.status === 201) {
          const savedBuilding = response.data;
          await this.fetchBuildings();

          if (!isUpdate) await this.addBuildingToList(savedBuilding);

          this.showEditModal = false;
          this.showSuccessMessage(
            isUpdate
              ? "Building updated successfully"
              : "Building created successfully"
          );
        } else {
          throw new Error(response.statusText);
        }
      } catch (error) {
        console.error("Error saving building:", error);
        this.showErrorMessage("Failed to save building, " + error.message);
      } finally {
        this.isSaving = false;
      }
    },

    async addBuildingToList(building) {
      if (building?.error) {
        this.showErrorMessage(building.error);
        throw new Error(building.error);
      }

      if (this.selectedBuildingList.find((item) => item.id === building.id)) {
        return;
      }

      this.selectedBuildingList.push(building);

      try {
        const response = await apiClient.put(`/add-building/${building.id}`, {
          selectedProblem: this.selectedProblem,
        });

        if (response.status !== 200) {
          throw new Error(response.statusText);
        }

        this.showSuccessMessage("Building added to selection");
      } catch (error) {
        console.error("Error adding building to list:", error);
        // Remove from local list if API call failed
        const index = this.selectedBuildingList.findIndex(
          (item) => item.id === building.id
        );
        if (index !== -1) {
          this.selectedBuildingList.splice(index, 1);
        }
        this.showErrorMessage("Failed to add building");
        throw error;
      }
    },

    async removeFromSelectedList(buildingId) {
      const index = this.selectedBuildingList.findIndex(
        (item) => item.id === buildingId
      );

      if (index === -1) return;

      const removedBuilding = this.selectedBuildingList[index];
      this.selectedBuildingList.splice(index, 1);

      try {
        const response = await apiClient.delete(
          `/delete-building/${buildingId}`
        );

        if (response.status !== 200) {
          throw new Error(response.statusText);
        }

        // Update selection if needed
        if (this.selectedBuildingId === buildingId) {
          this.selectedBuildingId =
            this.selectedBuildingList.length > 0
              ? this.selectedBuildingList[0].id
              : null;
        }
      } catch (error) {
        console.error("Error removing building:", error);
        // Restore the building to the list if API call failed
        this.selectedBuildingList.splice(index, 0, removedBuilding);
        this.showErrorMessage("Failed to remove building");
      }
    },

    async toggleBuildingSelection(building) {
      if (this.selectedBuildingIds.includes(building.id)) {
        await this.removeFromSelectedList(building.id);
      } else {
        await this.addBuildingToList(building);
      }
    },

    // ==================== Delete Operations ====================
    confirmDeleteBuilding(building) {
      this.buildingToDelete = building;
      this.confirmDialog = true;
      this.showEditModal = false;
    },

    async deleteBuilding() {
      if (!this.buildingToDelete) return;

      try {
        const response = await apiClient.delete(
          `/building/${this.buildingToDelete.id}`
        );

        if (response.status === 200) {
          await this.removeFromSelectedList(this.buildingToDelete.id);
          await this.fetchBuildings();
          this.confirmDialog = false;
          this.showSuccessMessage("Building deleted successfully");
        } else {
          throw new Error(response.statusText);
        }
      } catch (error) {
        console.error("Error deleting building:", error);
        this.showErrorMessage("Failed to delete building");
      } finally {
        this.buildingToDelete = null;
      }
    },

    // ==================== Notifications ====================
    showSuccessMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "success";
      this.snackbar = true;
    },

    showErrorMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "error";
      this.snackbar = true;
    },
  },

  watch: {
    selectedLab() {
      this.fetchBuildings();
    },
    selectedProblem() {
      this.fetchBuildings();
    },
  },
};
</script>

<style scoped>
.header-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
}

.buildings {
  max-height: 700px;
  overflow-y: auto;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.empty-state h3 {
  margin: 16px 0 8px;
}

/* Confirm Dialog */
.confirm-dialog {
  background-color: var(--secondaryColor) !important;
}

.confirm-dialog__title {
  color: var(--backgroundColor) !important;
}

.confirm-dialog__content {
  padding: 16px 24px;
}

.confirm-dialog__content p {
  margin-bottom: 8px;
  color: var(--backgroundColor);
}

.confirm-dialog__content .building-name {
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  text-align: center;
}

.confirm-dialog__content .warning-text {
  color: var(--error);
  font-size: 0.875rem;
  opacity: 0.8;
}

.confirm-dialog__actions {
  padding: 12px 16px;
}

/* Responsive */
@media (max-width: 600px) {
  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons {
    flex-direction: column;
  }

  .action-buttons .v-btn {
    width: 100%;
  }
}
</style>
